import {
  licenceFeatureSection,
  type ApplianceFeature,
  type Licence,
  type LicenceCatalog,
  type LicenceDownloadRow,
  type LicenceIssueOutcome,
  type LicenceKind,
} from "@xop/dashboard-shared"

import { api, apiBlob } from "@/lib/api"

// NOTE: `api()` JSON-encodes `body` itself. Never pre-stringify here — a string
// body is encoded AGAIN and reaches the BFF as a JSON string, which zod refuses
// with "Expected object, received string" (found live on 2026-09-11; pinned by
// licences-api.test.ts).

/**
 * Licence data access — the DASHBOARD BFF (`/api/licences`), not crm-api.
 *
 * Every other CRM page talks to crm-api directly through `crmApiUrl`. This one
 * deliberately does not, and the reason is a permission: `crm:licences` is a
 * DASHBOARD grant. crm-api's licences controller gates staff actors on
 * `crm:edit` and the customer data scope only — it has no notion of a
 * `crm:licences` page grant — so the BFF is the sole place that grant is
 * enforced for a staff actor. Calling crm-api directly from here would route
 * around it.
 *
 * (crm-api's own gate is real for RESELLER actors, which carry
 * `licences:view` / `licences:edit`; it is the staff side that leans on this
 * file.)
 */

export interface LicencePayload {
  customer_id?: number
  kind?: LicenceKind
  label?: string
  hardware_address?: string
  expires_on?: string | null
  status?: "draft" | "issued" | "revoked"
  notes?: string | null
  feature_keys?: string[]
  ports_total?: number
  ports_tdm?: number
  ports_audio_conferencing?: number
  ports_web_conferencing?: number
  ports_network_switches?: number
  ports_audio_recording?: number
}

export interface LicenceListFilters {
  customerId?: number
  status?: string
  expiringWithin?: number
  /** One reseller's licences: for its customers, and any it created. */
  resellerId?: number | string
}

export async function fetchLicences(filters: LicenceListFilters = {}): Promise<Licence[]> {
  const search = new URLSearchParams()
  if (filters.customerId) search.set("customerId", String(filters.customerId))
  if (filters.status) search.set("status", filters.status)
  if (filters.expiringWithin) search.set("expiringWithin", String(filters.expiringWithin))
  if (filters.resellerId) search.set("resellerId", String(filters.resellerId))
  const suffix = search.size > 0 ? `?${search.toString()}` : ""

  const body = await api<{ licences: Licence[] }>(`/api/licences${suffix}`)
  return body.licences
}

export async function fetchLicence(id: number): Promise<Licence> {
  const body = await api<{ licence: Licence }>(`/api/licences/${id}`)
  return body.licence
}

/** The catalogue the builder picks from (2026-09-15): CRM ▸ Pricing's rows in
 * the two licensable categories, each with the appliance key it is linked to
 * (null until curated), plus every key the appliance understands. The BFF
 * stamps `section` on every row; a row that reaches here without one — an
 * older BFF — gets the same rule applied, so the picker never has to derive it. */
export async function fetchLicenceCatalog(): Promise<LicenceCatalog> {
  const body = await api<Partial<LicenceCatalog>>("/api/licences/catalog")
  return {
    features: (body.features ?? []).map((feature) => ({
      ...feature,
      section: licenceFeatureSection(feature),
    })),
    applianceKeys: body.applianceKeys ?? [],
  }
}

/** The appliance's own key list with descriptions — what CRM ▸ Pricing offers
 * when linking a feature to the key its licence file will name. */
export async function fetchApplianceFeatures(): Promise<ApplianceFeature[]> {
  const body = await api<{ features: ApplianceFeature[] }>("/api/licences/appliance-features")
  return body.features
}

/** May this customer still get a TRIAL? Only a customer with no licence yet.
 * The builder asks before a licence exists, to offer (or grey out) the choice. */
export async function fetchTrialAvailability(customerId: number): Promise<boolean> {
  const body = await api<{ available: boolean }>(`/api/licences/trial-availability?customerId=${customerId}`)
  return body.available === true
}

/** WHOSE appliance is "ours" — the internal company, or null.
 *
 * It cannot be found by searching companies: crm-api hides it from every
 * customer listing on purpose. Refused (and journaled upstream) for anyone but
 * a super admin, which is why the caller treats a failure as "no entry to
 * offer" rather than an error worth showing. */
export async function fetchInternalCompany(): Promise<{ id: number; name: string } | null> {
  const body = await api<{ customer: { id: number; name: string } | null }>(
    "/api/licences/internal-company",
  )
  return body.customer
}

export async function createLicence(payload: LicencePayload): Promise<Licence> {
  const body = await api<{ licence: Licence }>("/api/licences", {
    method: "POST",
    body: payload,
  })
  return body.licence
}

export async function updateLicence(id: number, payload: LicencePayload): Promise<Licence> {
  const body = await api<{ licence: Licence }>(`/api/licences/${id}`, {
    method: "PATCH",
    body: payload,
  })
  return body.licence
}

export async function deleteLicence(id: number): Promise<void> {
  await api<null>(`/api/licences/${id}`, { method: "DELETE" })
}

export interface IssueResult {
  licence: Licence
  /** "issued" means a file exists. "approval_requested" means NOTHING was signed
   * and the CTO has been emailed. Read it — never assume Issue produced a file. */
  outcome: LicenceIssueOutcome
  warnings: Array<{ code: string; message: string }>
}

/** Press Issue. A trial signs now; anything longer is parked and the CTO is
 * emailed — `outcome` says which. */
export async function issueLicence(id: number): Promise<IssueResult> {
  return api<IssueResult>(`/api/licences/${id}/issue`, { method: "POST" })
}

/** Draft the SUCCESSOR of an issued licence — same appliance, features and
 * ports, a year longer unless `expiresOn` says otherwise. crm-api opens the
 * window 30 days before expiry; outside it this rejects with
 * `LICENCE_RENEWAL_TOO_EARLY` and a sentence naming the date. */
export async function renewLicence(id: number, options: { expiresOn?: string } = {}): Promise<Licence> {
  const body = await api<{ licence: Licence }>(`/api/licences/${id}/renew`, {
    method: "POST",
    body: options.expiresOn ? { expiresOn: options.expiresOn } : {},
  })
  return body.licence
}

/** The CTO's yes — signs in the same call. Needs crm:licences_approve. */
export async function approveLicence(id: number): Promise<Licence> {
  const body = await api<{ licence: Licence }>(`/api/licences/${id}/approve`, { method: "POST" })
  return body.licence
}

/** The CTO's no, with the reason the requester will read. */
export async function rejectLicence(id: number, note: string): Promise<Licence> {
  const body = await api<{ licence: Licence }>(`/api/licences/${id}/reject`, {
    method: "POST",
    body: { note },
  })
  return body.licence
}

/** This licence's download history — every pull, refusals included. */
export async function fetchLicenceDownloads(id: number): Promise<LicenceDownloadRow[]> {
  const body = await api<{ downloads: LicenceDownloadRow[] }>(`/api/licences/${id}/downloads`)
  return body.downloads
}

/** The signed ZIP. Counted and audited upstream — never fetch it speculatively. */
export async function downloadLicenceFile(id: number): Promise<Blob> {
  return apiBlob(`/api/licences/${id}/file`)
}

export async function liftDownloadCap(id: number, additional: number): Promise<Licence> {
  const body = await api<{ licence: Licence }>(`/api/licences/${id}/lift_cap`, {
    method: "POST",
    body: { additional },
  })
  return body.licence
}
